import { urlFor } from "@/utils/client";
import { IButton, ICard, ICards, IImageWithCaption } from "@/utils/types";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";

function CardButtons({ buttons }: { buttons: IButton[] | undefined }) {
  return (
    <div className="flex italic">
      {buttons?.map((button, i) => {
        if (!button.link) return null;
        if (button.link.includes("http")) {
          return (
            <a
              className="flex items-center gap-2"
              target="_blank"
              rel="noreferrer"
              key={i}
              href={button.link}
            >
              {button.label}
              <svg
                width="10"
                height="11"
                viewBox="0 0 10 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.24358 6.16363H0.214722V4.83655H7.24358L4.05364 1.6466L4.99997 0.714844L9.78521 5.50009L4.99997 10.2853L4.05364 9.35357L7.24358 6.16363Z"
                  fill="#000"
                />
              </svg>
            </a>
          );
        }
        return (
          <Link className="flex items-center gap-2" key={i} href={button.link}>
            {button.label}
            <svg
              width="10"
              height="11"
              viewBox="0 0 10 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.24358 6.16363H0.214722V4.83655H7.24358L4.05364 1.6466L4.99997 0.714844L9.78521 5.50009L4.99997 10.2853L4.05364 9.35357L7.24358 6.16363Z"
                fill="#000"
              />
            </svg>
          </Link>
        );
      })}
    </div>
  );
}

/** Prefer main image for full-bleed hero; fall back to logo when editors use Logo only. */
function resolveFullBleedSource(card: ICard): IImageWithCaption | null {
  if (card.image?.image) return card.image;
  if (card.logo?.image) return card.logo;
  return null;
}

function CardMedia({
  card,
  fullBleed,
  bleedSource,
  imageSizes,
}: {
  card: ICard;
  fullBleed: boolean;
  bleedSource: IImageWithCaption | null;
  imageSizes: string;
}) {
  if (fullBleed && bleedSource?.image) {
    return (
      <div className="relative w-full aspect-[4/3] shrink-0">
        <Image
          fill
          sizes={imageSizes}
          alt={bleedSource.caption ?? ""}
          src={urlFor(bleedSource.image).url()}
          className="object-cover"
        />
      </div>
    );
  }
  if (card.logo?.image) {
    return (
      <Image
        width={128}
        height={64}
        sizes="128px"
        alt={card.logo?.caption ?? ""}
        src={urlFor(card.logo.image).url()}
        className="object-contain w-32 "
      />
    );
  }
  if (!card.image?.image) return null;
  return (
    <Image
      width={640}
      height={360}
      sizes={imageSizes}
      alt={card.image?.caption ?? ""}
      src={urlFor(card.image.image).url()}
      className="w-full object-cover aspect-video"
    />
  );
}

export function Cards(props: ICards) {
  const router = useRouter();
  const imageSizes =
    router.pathname === "/"
      ? "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
      : "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 50vw";

  return (
    <div className="flex flex-col items-center component-gap component-padding text-white text-center">
      {props.title && (
        <div
          style={{
            color: props.accentColor ? props.accentColor : "#000",
          }}
          className="flex flex-col component-gap max-w-5xl"
        >
          <p className="heading">{props.title}</p>
          <p className="opacity-80">{props.description}</p>
        </div>
      )}
      <div
        className={`grid grid-cols-1 ${
          router.pathname === "/"
            ? `md:grid-cols-2 lg:grid-cols-3`
            : `md:grid-cols-2 lg:grid-cols-2`
        } component-gap`}
      >
        {(props.cards ?? []).map((card, i) => {
          const bleedSource = resolveFullBleedSource(card);
          const fullBleed =
            Boolean(props.fullWidthCardImages) && bleedSource != null;

          return (
            <div
              style={{
                backgroundColor: props.accentColor || undefined,
                color: props.backgroundColor || undefined,
              }}
              key={i}
              className={`bg-white gap-4 text-black flex flex-col h-fit justify-start text-left ${
                fullBleed ? "overflow-hidden" : "p-8"
              }`}
            >
              {fullBleed ? (
                <>
                  <CardMedia
                    card={card}
                    fullBleed
                    bleedSource={bleedSource}
                    imageSizes={imageSizes}
                  />
                  <div className="p-8 flex flex-col gap-4">
                    <p className="font-display">{card.title}</p>
                    <p className="opacity-80">{card.description}</p>
                    <CardButtons buttons={card.buttons} />
                  </div>
                </>
              ) : (
                <>
                  <CardMedia
                    card={card}
                    fullBleed={false}
                    bleedSource={null}
                    imageSizes={imageSizes}
                  />
                  <p className="font-display">{card.title}</p>
                  <p className="opacity-80">{card.description}</p>
                  <CardButtons buttons={card.buttons} />
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
